// Бригады и бригадиры объекта (встреча 24.10.2026): бригадир, люди по специальностям, вахты; журнал
// «кто когда работал» — по отчётам смен, где прораб отмечает бригадира.

import { useState } from "react";
import { addDays, fmtDate, localISODate } from "../../shared/format";
import { Button, Drawer, Empty, Field, Input, Loading, Select, useAsync, useToast } from "../../shared/ui";
import { api } from "../ctx";

type Part = { key?: string; title: string; brigadier: string; phone: string; composition: Record<string, string> };

export function BrigadesTab({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const l = useAsync<any[]>(() => api.get(`/projects/${projectId}/brigades`), [projectId]);
  const crews = useAsync<any[]>(() => api.get("/crews"), []);
  const [edit, setEdit] = useState<any | null>(null);
  if (!l.data) return <Loading error={l.error} onRetry={l.reload} />;
  return (
    <div className="stack">
      {canEdit && <div><Button variant="primary" onClick={() => setEdit({})}>Новая бригада</Button></div>}
      {l.data.length === 0 ? <Empty>Бригад пока нет.{canEdit ? " Заведите бригаду: бригадир и сколько людей каких специальностей." : ""}</Empty> : (
        <div className="cols-3">{l.data.map((b) => (
          <div key={b.id} className={`panel pad stack tight${b.active ? "" : " muted"}`}>
            <div className="row between"><h3>{b.name}</h3>{!b.active && <span className="tag plain">не на объекте</span>}</div>
            {b.parts.map((p: any) => (
              <div key={p.key} className="small">
                {b.parts.length > 1 && <b>{p.title}: </b>}бригадир <b>{p.brigadier}</b>{p.phone ? `, ${p.phone}` : ""}
                <div className="muted">{p.composition_text || "состав не указан"} · всего {p.people}</div>
              </div>
            ))}
            {b.note && <div className="small muted">{b.note}</div>}
            {canEdit && <div><Button size="small" onClick={() => setEdit(b)}>Изменить</Button></div>}
          </div>
        ))}</div>
      )}
      <p className="small muted">Бригадира в отчёте смены отмечает прораб — так в журнале видно, кто когда работал. Замена бригадира не переписывает прошлые смены. Вахта: одна бригада из двух частей, у каждой свой бригадир.</p>
      {edit && <BrigadeDrawer projectId={projectId} b={edit} crews={crews.data || []} onClose={() => setEdit(null)} onDone={() => { setEdit(null); l.reload(); }} />}
    </div>
  );
}

function BrigadeDrawer({ projectId, b, crews, onClose, onDone }: { projectId: string; b: any; crews: any[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const isNew = !b.id;
  const toPart = (p: any): Part => ({ key: p.key, title: p.title || "", brigadier: p.brigadier || "", phone: p.phone || "", composition: Object.fromEntries(Object.entries(p.composition || {}).map(([k, n]) => [k, String(n)])) });
  const [name, setName] = useState<string>(b.name || "");
  const [note, setNote] = useState<string>(b.note || "");
  const [active, setActive] = useState<boolean>(b.active ?? true);
  const [parts, setParts] = useState<Part[]>(b.parts?.length ? b.parts.map(toPart) : [{ title: "", brigadier: "", phone: "", composition: {} }]);
  const setPart = (i: number, p: Partial<Part>) => setParts(parts.map((x, k) => (k === i ? { ...x, ...p } : x)));
  const bad = parts.some((p) => p.brigadier.trim().length < 2 || Object.values(p.composition).some((n) => n !== "" && !/^\d{1,3}$/.test(n)));
  const save = async () => {
    const body = {
      name: name.trim(), note: note.trim() || null, active,
      parts: parts.map((p) => ({ key: p.key, title: p.title.trim() || null, brigadier: p.brigadier.trim(), phone: p.phone.trim() || null, composition: Object.fromEntries(Object.entries(p.composition).filter(([, n]) => n !== "" && Number(n) > 0).map(([k, n]) => [k, Number(n)])) })),
    };
    try {
      if (isNew) await api.post(`/projects/${projectId}/brigades`, body); else await api.put(`/brigades/${b.id}`, body);
      toast.ok(isNew ? "Бригада заведена" : "Бригада сохранена");
      onDone();
    } catch (e) { toast.error(e); }
  };
  const remove = async () => {
    try {
      const r = await api.del(`/brigades/${b.id}`);
      toast.ok(r?.result === "inactive" ? "У бригады есть смены в отчётах — она отмечена «не на объекте»" : "Бригада удалена");
      onDone();
    } catch (e) { toast.error(e); }
  };
  return (
    <Drawer title={isNew ? "Новая бригада" : b.name} onClose={onClose} footer={<>
      <Button onClick={onClose}>Отмена</Button>
      {!isNew && <Button variant="ghost" onClick={remove}>Удалить</Button>}
      <Button variant="primary" disabled={name.trim().length < 2 || bad} onClick={save}>{isNew ? "Завести" : "Сохранить"}</Button>
    </>}>
      <div className="stack">
        <Field label="Название"><Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Например: Бригада Иванова" /></Field>
        {parts.map((p, i) => (
          <div key={i} className="panel pad stack tight">
            <div className="row between">
              <h3>{parts.length > 1 ? `Часть ${i + 1} (вахта)` : "Бригадир и состав"}</h3>
              {parts.length > 1 && <Button size="small" variant="ghost" onClick={() => setParts(parts.filter((_, k) => k !== i))}>Убрать часть</Button>}
            </div>
            <div className="form-grid">
              {parts.length > 1 && <Field label="Название части"><Input value={p.title} onChange={(e) => setPart(i, { title: e.target.value })} placeholder={`Вахта ${i + 1}`} /></Field>}
              <Field label="Бригадир" hint="Заболел или ушёл — впишите нового; прошлые смены сохранят прежнего"><Input value={p.brigadier} onChange={(e) => setPart(i, { brigadier: e.target.value })} placeholder="Иванов И.И." /></Field>
              <Field label="Телефон"><Input value={p.phone} onChange={(e) => setPart(i, { phone: e.target.value })} placeholder="+7 900 000-00-00" /></Field>
            </div>
            <div className="small muted">Людей по специальностям:</div>
            <div className="form-grid">
              {crews.map((c) => (
                <Field key={c.code} label={`${c.name} (${c.code})`}>
                  <Input inputMode="numeric" value={p.composition[c.code] ?? ""} placeholder="0"
                    onChange={(e) => setPart(i, { composition: { ...p.composition, [c.code]: e.target.value.replace(/\D/g, "") } })} />
                </Field>
              ))}
            </div>
          </div>
        ))}
        {parts.length < 4 && <div><Button size="small" onClick={() => setParts([...parts, { title: "", brigadier: "", phone: "", composition: { ...parts[parts.length - 1].composition } }])}>+ вахта (вторая часть бригады)</Button></div>}
        <Field label="Примечание"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Например: вахта 15 дней, заезд по понедельникам" /></Field>
        {!isNew && <label className="check"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Работает на объекте (иначе её не выбрать в отчёте)</label>}
        <p className="small muted">Специальностей не хватает (сварщики, электрики на прогреве…) — «Администрирование» → «Справочники» → «Специальности».</p>
      </div>
    </Drawer>
  );
}

export function JournalTab({ projectId }: { projectId: string }) {
  const [brigade, setBrigade] = useState("");
  const [from, setFrom] = useState(addDays(localISODate(), -30));
  const l = useAsync<any[]>(() => api.get(`/projects/${projectId}/brigades`), [projectId]);
  const d = useAsync<any>(() => api.get(`/projects/${projectId}/brigades/journal`, { ...(brigade ? { brigade } : {}), date_from: from }), [projectId, brigade, from]);
  return (
    <div className="stack">
      <div className="row">
        <Select aria-label="Бригада" style={{ width: 240 }} value={brigade} onChange={(e) => setBrigade(e.target.value)} options={[{ value: "", label: "Все бригады" }, ...(l.data || []).map((b) => ({ value: b.id, label: b.name }))]} />
        <Select aria-label="Период" style={{ width: 200 }} value={from} onChange={(e) => setFrom(e.target.value)} options={[
          { value: addDays(localISODate(), -7), label: "Последние 7 дней" }, { value: addDays(localISODate(), -30), label: "Последние 30 дней" }, { value: addDays(localISODate(), -365), label: "За год" },
        ]} />
      </div>
      {!d.data ? <Loading error={d.error} onRetry={d.reload} /> : d.data.rows.length === 0 ? <Empty>Смен с отмеченным бригадиром за период нет. Бригадира отмечает прораб в отчёте смены — шаг «2. Люди и смена».</Empty> : (
        <>
          <div className="panel table-wrap">
            <table className="table">
              <thead><tr><th>Бригада</th><th>Бригадир</th><th>Работал</th><th className="num">Смен</th><th className="num">Людей в среднем</th></tr></thead>
              <tbody>{d.data.summary.map((x: any) => (
                <tr key={`${x.brigade}|${x.brigadier}`}><td>{x.brigade || "—"}</td><td><b>{x.brigadier || "—"}</b></td><td>{fmtDate(x.first)} — {fmtDate(x.last)}</td><td className="num">{x.shifts}</td><td className="num">{String(x.avg_people).replace(".", ",")}</td></tr>
              ))}</tbody>
            </table>
          </div>
          <div className="panel table-wrap">
            <table className="table">
              <thead><tr><th>Смена</th><th>Корпус</th><th>Бригада</th><th>Бригадир</th><th>Люди</th></tr></thead>
              <tbody>{d.data.rows.map((x: any, i: number) => (
                <tr key={i}><td>{fmtDate(x.date)}, {x.shift === "day" ? "день" : "ночь"}</td><td>{x.building}</td><td>{x.brigade || <span className="muted">не из списка</span>}{x.part ? <span className="muted"> · {x.part}</span> : null}</td><td>{x.brigadier || "—"}</td><td className="small">{x.people} — {x.headcount || "—"}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
